<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'
import { cn } from '@ds/lib/utils'
import { InputSize, SIZE_CLASS } from '.'

const props = defineProps<{
  defaultValue?: string | number
  modelValue?: string | number
  class?: HTMLAttributes['class']
  size?: InputSize
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

const inputSizeClass = computed(() => SIZE_CLASS[props.size ?? 'md'])
</script>

<template>
  <input
    v-model="modelValue"
    :class="cn(
      // `ds-control` opts this element out of @tailwindcss/forms' base styles,
      // most importantly its :focus ring. See src/assets/styles/ds-base.css,
      // which also supplies the design system's Iris focus ring.
      'ds-control',
      'flex w-full',
      // Height and horizontal padding only — the canonical component drives
      // vertical rhythm from the height, with no vertical padding.
      inputSizeClass,
      'bg-card text-foreground placeholder:text-foreground/35',
      // Inter at Medium (500) — the design system's UI workhorse weight.
      'font-ds font-medium',
      'border border-input rounded-ds-md',
      'transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard',
      // Canonical disabled state is the sunken surface, not reduced opacity.
      'disabled:bg-muted disabled:cursor-not-allowed',
      props.class,
    )"
  >
</template>
